import { StyleSheet, View, ViewProps } from 'react-native'
import { useStore, Draft, FloorplanData, FloorplanWorkspaceData } from 'zens-store'
import { Color, Spacing, Text, Button, Font, PopUp, Badge, Toast, ToastInfo, Icon } from 'zens-storybook'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

import { MenuMore } from '../menu'
import { NavigationDelete } from '../navigation'

import FloorplanDetail from './detail'
import FloorplanOverview from './overview'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  draft: {
    zIndex: 200,
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: Spacing.S,
    flexDirection: 'row',
    height: 74,
    borderBottomWidth: 1,
    borderColor: Color.calmGrey,
  },
  button: {
    marginRight: Spacing.S,
  },
  more: {
    backgroundColor: Color.simplyWhite,
    borderBottomColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
  moreTitle: {
    fontFamily: Font.medium,
  },
  delete: {
    backgroundColor: Color.soothingPink,
  },
  deleteIcon: {
    color: Color.simplyWhite,
  },
  deleteText: {
    color: Color.simplyWhite,
  },
  deleteHovered: {
    backgroundColor: Color.darkRed,
  },
  popup: {
    maxWidth: 600,
  },
  confirm: {
    marginBottom: Spacing.M,
  },
  badge: {
    marginRight: Spacing.L,
    paddingHorizontal: Spacing.S,
  },
  hintTitle: {
    color: Color.metalGrey,
  },
  hintText: {
    fontSize: 14,
    textAlign: 'center',
  },
  skeletonMore: {
    width: 40,
    alignItems: 'center',
  },
})

interface Props extends ViewProps {
  draft: Draft<FloorplanData>
  onSaveDraft?: (draft: Draft<FloorplanData>) => Promise<void>
  onSaveWorkspace?: (draft: Draft<FloorplanWorkspaceData>) => Promise<void>
  onRemoveWorkspace?: (draft: Draft<FloorplanWorkspaceData>) => Promise<void>
  onDuplicateWorkspaces?: (workspaces: FloorplanWorkspaceData[]) => Promise<FloorplanWorkspaceData[] | void>
  onPublish?: (draft: Draft<FloorplanData>) => Promise<void>
  onDiscard?: (draft: Draft<FloorplanData>) => Promise<void>
  onSettings?: () => void
  errors?: Record<string, string>
}

const FloorplanPlacement = (props: Props) => {
  const {
    draft,
    onSaveDraft,
    onSaveWorkspace,
    onRemoveWorkspace,
    onDuplicateWorkspaces,
    onPublish,
    onDiscard,
    onSettings,
    errors,
    style,
    ...otherProps
  } = props
  const [workspaceId, setWorkspaceId] = useState<number>()
  const [confirmAdd, setConfirmAdd] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Draft<FloorplanWorkspaceData>>()
  const [hints, setHints] = useState<ToastInfo[]>([])
  const { storage } = useStore()
  const { company_id: companyId, building_id: buildingId } = draft.data
  const { t } = useTranslation()

  async function saveWorkspace(draft: Draft<FloorplanWorkspaceData>) {
    await onSaveWorkspace?.(draft)
    // draft.update('workspaces', data.workspaces)
    setWorkspaceId(undefined)
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setHints([
        {
          id: '1',
          message: renderHint(),
          status: 'light',
          closeable: true,
        },
      ])
    }, 1000)
    return () => clearTimeout(timeout)
  }, [])

  function more(value: string | string[]) {
    switch (value) {
      case 'settings':
        onSettings?.()
        break
      case 'delete':
        onDiscard?.(draft)
        break
    }
  }

  function update(data: FloorplanData) {
    draft.update('workspaces', data.workspaces)
  }

  function add() {
    storage.get(`${companyId}:${buildingId}:add`, undefined, (value) => {
      if (value === '1') {
        setWorkspaceId(NaN)
      } else {
        setConfirmAdd(true)
      }
    })
  }

  function remove(workspaceId: number) {
    setConfirmDelete(new Draft({ id: 0, ...draft.data.workspaces?.find((item) => item.id === workspaceId) }))
  }

  async function removeWorkspace(value: Draft<FloorplanWorkspaceData>) {
    await onRemoveWorkspace?.(value)
    draft.update(
      'workspaces',
      draft.data.workspaces?.filter((item) => item.id !== value.data.id),
    )
    setConfirmDelete(undefined)
    if (workspaceId === value.data.id) setWorkspaceId(undefined)
  }

  function addConfirm() {
    storage.set(`${companyId}:${buildingId}:add`, '1', undefined, () => {
      setConfirmAdd(false)
      add()
    })
  }

  function closeHint() {
    setHints([])
  }

  function renderHint() {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    const key = isMac ? 'Cmd' : 'Ctrl'
    return (
      <View>
        <Text style={[styles.hintText, styles.hintTitle]}>
          <Trans>Key hint for quick editing</Trans>
        </Text>
        <Text style={styles.hintText}>
          <Trans>Hold Shift to select multiple workspaces.</Trans>
        </Text>
        <Text style={styles.hintText}>
          <Trans>
            Use {{ key }} + C to copy and {{ key }} + V to paste workspaces.
          </Trans>
        </Text>
        <Text style={styles.hintText}>
          <Trans>Use Shift key during rotation for custom angles.</Trans>
        </Text>
      </View>
    )
  }

  function renderButtons() {
    return (
      <View style={styles.draft}>
        {!draft.data.floorplan?.published && <Badge title={t('Draft')} light style={styles.badge} />}
        <Button
          title={t('Save and exit')}
          light
          style={styles.button}
          onPress={() => onSaveDraft?.(draft)}
          testID="FloorplanPlacement.Save"
        />
        <Button title={t('Publish')} onPress={() => onPublish?.(draft)} testID="FloorplanPlacement.Publish" />
        <MenuMore
          items={[
            {
              key: 'title',
              title: t('Floorplan options'),
              disabled: true,
              style: styles.more,
              textStyle: styles.moreTitle,
            },
            {
              key: 'settings',
              icon: 'gear',
              title: t('Floor settings'),
              testID: 'FloorplanPlacement.Settings',
            },
            {
              key: 'delete',
              icon: 'trash',
              title: t('Delete draft'),
              style: styles.delete,
              textStyle: styles.deleteText,
              iconStyle: styles.deleteIcon,
              hoveredStyle: styles.deleteHovered,
              testID: 'FloorplanPlacement.Discard',
            },
          ]}
          onSelect={more}
          testID="FloorplanPlacement.More"
        />
      </View>
    )
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      {workspaceId !== undefined ? (
        <FloorplanDetail
          data={draft.data}
          workspaceId={workspaceId}
          onUpdate={update}
          onSave={saveWorkspace}
          onCancel={async () => setWorkspaceId(undefined)}
          onRemove={setConfirmDelete}
          errors={errors}
        />
      ) : (
        <FloorplanOverview
          draft={draft}
          renderHeader={renderButtons}
          onSelect={setWorkspaceId}
          onPlace={setWorkspaceId}
          onAdd={add}
          onRemove={remove}
          onDuplicate={onDuplicateWorkspaces}
          errors={errors}
        />
      )}
      <PopUp visible={confirmAdd} style={styles.popup} onClose={() => setConfirmAdd(false)}>
        <Text level={4} style={styles.confirm}>
          <Trans>Workspace library impact notice</Trans>
        </Text>
        <Text style={styles.confirm}>
          <Trans>
            Adding workspaces to the workspace library on the left will automatically add them to other pages on the
            platform, to ensure consistency over the entire platform.
          </Trans>
        </Text>
        <Text style={styles.confirm}>
          <Trans>
            It is important to be aware of the implications this might have. All changes to the workspace library
            immediately affect the features that include workspaces.
          </Trans>
        </Text>
        <Button title={t('Got it')} onPress={addConfirm} testID="FloorplanPlacement.AddConfirm" />
      </PopUp>
      <NavigationDelete
        entity={t('Workspace')}
        visible={!!confirmDelete}
        onClose={() => setConfirmDelete(undefined)}
        onDelete={() => removeWorkspace(confirmDelete!)}
      />
      <Toast messages={hints} onClose={closeHint} />
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <FloorplanOverview.Skeleton
        header={
          <View style={styles.draft}>
            <Button.Skeleton style={styles.button} />
            <Button.Skeleton />
            <View style={styles.skeletonMore}>
              <Icon name="dots" color={Color.calmGrey} />
            </View>
          </View>
        }
      />
    </View>
  )
}
FloorplanPlacement.Skeleton = Skeleton

export default observer(FloorplanPlacement)
